import { isClientView } from '../common/organizationAccessMode';
import React, { useRef, useEffect, useState } from 'react';
import { ICONS } from './constants';
import { createSafeMarkdown } from '../utils/safeMarkdown';
import type { Page, NavParams, HelpRequest } from '@types';
import { getFunctions, httpsCallable } from 'firebase/functions';

const md = createSafeMarkdown();

interface Message {
    sender: 'user' | 'assistant';
    content: string;
}

interface ChatBotProps {
    industry: string;
    onClose: () => void;
    onNavigate: (nav: { page: Page; params?: NavParams }) => void;
    initialContext: HelpRequest | null;
    embedded?: boolean;
    fullWorkspaceAccess?: boolean;
}

const ChatBot: React.FC<ChatBotProps> = ({ industry, onClose, onNavigate, initialContext, embedded = false, fullWorkspaceAccess = false }) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState('');
    const messagesContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let initialMessage = fullWorkspaceAccess
            ? 'I can help with your consultant dashboard tools and workflows. What would you like to do?'
            : 'I can help you use your client dashboard, understand delivered intelligence, and find the right consultant service. What would you like to do?';

        if (initialContext) {
            initialMessage = fullWorkspaceAccess
                ? `You asked for help with **${initialContext.workflowName || initialContext.suiteName}**. What would you like to do?`
                : `You asked about **${initialContext.workflowName || initialContext.suiteName}**. I can help you find the client steps or service request for that goal.`;
        }

        setMessages([{ sender: 'assistant', content: initialMessage }]);
    }, [industry, initialContext, fullWorkspaceAccess]);
    
    const scrollToBottom = () => {
        const container = messagesContainerRef.current;
        if (container)
            container.scrollTo({ top: container.scrollHeight, behavior: messages.length > 1 ? 'smooth' : 'auto' });
    };

    useEffect(scrollToBottom, [messages]);

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault(); 
        const userText = inputValue.trim();
        if (!userText) return;
    
        // 1. Add user message to state
        const userMessage: Message = { sender: 'user', content: userText };
        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
    
        // 2. Add a temporary "Thinking..." message
        const thinkingMessage: Message = { sender: 'assistant', content: '...' };
        setMessages(prev => [...prev, thinkingMessage]);
    
        try {
            // 3. CALL THE REAL BACKEND with history and context
            const functions = getFunctions();
            const chatBotProxy = httpsCallable(functions, 'chatBotProxy');
            
            // Filter out the initial greeting and the temporary thinking message for history
            const history = messages.slice(1).filter(m => m.content !== '...');

            const response: any = await chatBotProxy({ 
                message: userText,
                history,
                context: initialContext,
                industry
            });
            const aiResponse = response.data.result;
    
            // 4. Replace "Thinking..." with the real answer
            setMessages(prev => [
                ...prev.slice(0, -1), 
                { sender: 'assistant', content: aiResponse }
            ]);
        } catch (error) {
            console.error("Chat Error:", error);
            setMessages(prev => [
                ...prev.slice(0, -1), 
                { sender: 'assistant', content: "I'm sorry, I encountered an operational interruption. Please try again or check the Instruction Manual." }
            ]);
        }
    };

    return (
        <div className={`${embedded ? 'relative h-[32rem] w-full' : 'fixed bottom-4 right-4 md:right-8 w-[calc(100%-2rem)] max-w-md h-[70%] max-h-[500px] z-50'} bg-white rounded-xl shadow-2xl border border-slate-200 flex flex-col transition-all duration-300 ease-in-out animate-fade-in-up`}>
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
                <h3 className="font-bold text-slate-800">{fullWorkspaceAccess ? 'IntelOptics Insights Assistant' : 'IntelOptics Client Assistant'}</h3>
                {!embedded && <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700 rounded-full">
                     <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>}
            </div>

            {/* Messages */}
            <div ref={messagesContainerRef} className="flex-1 p-4 overflow-y-auto">
                <div className="space-y-4">
                    {messages.map((msg, index) => (
                        <div key={index} className={`flex items-end gap-2 ${msg.sender === 'user' ? 'justify-end' : ''}`}>
                            {msg.sender === 'assistant' && (
                                <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                                    {ICONS.logo}
                                </div>
                            )}
                            <div
                                className={`prose prose-sm max-w-xs md:max-w-sm rounded-xl p-3 ${msg.sender === 'user' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-800'}`}
                                dangerouslySetInnerHTML={{ __html: md.render(msg.content || '') }}
                            ></div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Input */}
            <form onSubmit={handleSendMessage} className="p-3 border-t border-slate-200 bg-white rounded-b-xl flex items-center">
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Ask a follow-up question..."
                    className="flex-1 bg-slate-100 border-transparent focus:border-blue-500 focus:ring-blue-500 rounded-lg p-2 text-sm"
                    disabled={isClientView}
                />
                <button 
                    type="submit" 
                    disabled={isClientView || !inputValue.trim()}
                    className="ml-2 p-2 bg-blue-600 text-white rounded-full disabled:bg-slate-400 hover:bg-blue-700 transition-colors"
                >
                    {ICONS.send}
                </button>
            </form>
            </div>
        );
    };

export default ChatBot;
